import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import {
    getAllProfiles,
    getSmsConfigurations,
    linkAnswerToProfile,
    Profile,
    SmsConfiguration,
    unlinkAnswerFromProfile,
    updateSmsConfiguration
} from '@/lib/forward-api'
import { Answer, useCustomerStore } from '@/stores/customer-store'
import { Forward, MessageSquare, Phone } from 'lucide-react'
import { useEffect, useState } from 'react'

interface AnswerDialogProps {
    answer: Answer | null
    isOpen: boolean
    onClose: () => void
    onSave: (answer: Answer) => Promise<void>
    isNewAnswer?: boolean
}

export function AnswerDialog({
    answer,
    isOpen,
    onClose,
    onSave,
    isNewAnswer = false
}: AnswerDialogProps) {
    const { toast } = useToast()
    const { loadCallHandlingProfiles } = useCustomerStore()
    const [activeTab, setActiveTab] = useState('answer')
    const [formData, setFormData] = useState<Answer>(() =>
        answer || {
            id: '',
            title: '',
            guestMessage: '',
            aiResponse: '',
            notes: '',
            editableByCustomer: true,
            smsEnabled: false,
            forwardingEnabled: false,
            active: true,
            priority: 1,
        }
    )
    const [isLoading, setIsLoading] = useState(false)

    // Forwarding state
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [selectedProfileId, setSelectedProfileId] = useState<string>('-1')
    const [forwardingLoading, setForwardingLoading] = useState(false)

    // SMS state
    const [smsConfig, setSmsConfig] = useState<SmsConfiguration>({
        activeOnAction: '',
        messageToSend: '',
        utteranceToSay: '',
        smsActive: false
    })
    const [smsLoading, setSmsLoading] = useState(false)

    // Load profiles and SMS config when dialog opens
    useEffect(() => {
        if (isOpen && answer?.id) {
            loadForwardingData()
            loadSmsData()
        }
    }, [isOpen, answer?.id])

    // Update form data when answer prop changes
    useEffect(() => {
        if (answer) {
            setFormData(answer)
        }
    }, [answer])

    const loadForwardingData = async () => {
        if (!answer?.id) return
        setForwardingLoading(true)
        try {
            const allProfiles = await getAllProfiles()
            setProfiles(allProfiles)
            // Find profile linked to this answer
            const linkedProfile = allProfiles.find(p => p.usedByActions?.includes(answer.id))
            setSelectedProfileId(linkedProfile?.id || '-1')
        } catch (error) {
            console.error('Failed to load profiles:', error)
        } finally {
            setForwardingLoading(false)
        }
    }

    const loadSmsData = async () => {
        if (!answer?.id) return
        setSmsLoading(true)
        try {
            const configs = await getSmsConfigurations()
            const config = configs.find(c => c.activeOnAction === answer.id)
            if (config) {
                setSmsConfig(config)
            } else {
                setSmsConfig({
                    activeOnAction: answer.id,
                    messageToSend: '',
                    utteranceToSay: '',
                    smsActive: false
                })
            }
        } catch (error) {
            console.error('Failed to load SMS config:', error)
        } finally {
            setSmsLoading(false)
        }
    }

    const handleForwardingChange = async (profileId: string) => {
        if (!answer?.id) return
        setForwardingLoading(true)
        try {
            if (profileId === '-1') {
                await unlinkAnswerFromProfile(answer.id)
                setFormData(prev => ({ ...prev, forwardingEnabled: false }))
                toast({ title: 'Erfolg', description: 'Weiterleitung deaktiviert.' })
            } else {
                await linkAnswerToProfile(answer.id, profileId)
                setFormData(prev => ({ ...prev, forwardingEnabled: true }))
                toast({ title: 'Erfolg', description: 'Weiterleitung aktiviert.' })
            }
            setSelectedProfileId(profileId)
            loadCallHandlingProfiles()
        } catch (error) {
            toast({ title: 'Fehler', description: 'Weiterleitung konnte nicht geändert werden.', variant: 'destructive' })
        } finally {
            setForwardingLoading(false)
        }
    }

    const handleSmsUpdate = async () => {
        if (!answer?.id) return
        setSmsLoading(true)
        try {
            await updateSmsConfiguration({
                ...smsConfig,
                activeOnAction: answer.id
            })
            setFormData(prev => ({ ...prev, smsEnabled: smsConfig.smsActive }))
            toast({ title: 'Erfolg', description: 'SMS-Konfiguration gespeichert.' })
        } catch (error) {
            toast({ title: 'Fehler', description: 'SMS-Konfiguration konnte nicht gespeichert werden.', variant: 'destructive' })
        } finally {
            setSmsLoading(false)
        }
    }

    const handleSave = async () => {
        // Basic validation
        if (!formData.title?.trim()) {
            toast({
                title: "Validierungsfehler",
                description: "Bitte gib einen Titel ein.",
                variant: "destructive"
            })
            return
        }

        if (!formData.aiResponse?.trim()) {
            toast({
                title: "Validierungsfehler",
                description: "Bitte gib eine KI-Antwort ein.",
                variant: "destructive"
            })
            return
        }

        setIsLoading(true)
        try {
            await onSave(formData)
            toast({
                title: isNewAnswer ? "Antwort erstellt" : "Antwort aktualisiert",
                description: `Die Antwort "${formData.title}" wurde erfolgreich ${isNewAnswer ? 'erstellt' : 'gespeichert'}.`
            })
            onClose()
        } catch (error) {
            toast({
                title: "Fehler",
                description: `Die Antwort konnte nicht ${isNewAnswer ? 'erstellt' : 'gespeichert'} werden.`,
                variant: "destructive"
            })
        } finally {
            setIsLoading(false)
        }
    }

    const updateFormData = (field: keyof Answer, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isNewAnswer ? 'Neue Antwort erstellen' : 'Antwort bearbeiten'}
                    </DialogTitle>
                    <DialogDescription>
                        {isNewAnswer
                            ? 'Erstelle eine neue automatische Antwort für deine Kunden.'
                            : 'Bearbeite die Details dieser automatischen Antwort.'
                        }
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-3 mb-4">
                        <TabsTrigger value="answer" className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Antwort
                        </TabsTrigger>
                        <TabsTrigger value="forwarding" className="flex items-center gap-2" disabled={isNewAnswer}>
                            <Forward className="h-4 w-4" />
                            Weiterleitung
                        </TabsTrigger>
                        <TabsTrigger value="sms" className="flex items-center gap-2" disabled={isNewAnswer}>
                            <Phone className="h-4 w-4" />
                            SMS
                        </TabsTrigger>
                    </TabsList>

                    {/* Answer Tab */}
                    <TabsContent value="answer" className="space-y-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">Grundinformationen</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Titel *</Label>
                                    <Input
                                        id="title"
                                        value={formData.title}
                                        onChange={(e) => updateFormData('title', e.target.value)}
                                        placeholder="z.B. Begrüßung Neukunden"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="priority">Priorität</Label>
                                    <Select
                                        value={formData.priority?.toString() || '1'}
                                        onValueChange={(value) => updateFormData('priority', parseInt(value))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Niedrig</SelectItem>
                                            <SelectItem value="2">Normal</SelectItem>
                                            <SelectItem value="3">Hoch</SelectItem>
                                            <SelectItem value="4">Sehr hoch</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="guestMessage">Auslöser (Gast-Nachricht)</Label>
                                <Input
                                    id="guestMessage"
                                    value={formData.guestMessage || ''}
                                    onChange={(e) => updateFormData('guestMessage', e.target.value)}
                                    placeholder="z.B. Hallo, ich brauche Hilfe"
                                />
                            </div>
                        </div>

                        {/* Response Content */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">Antwortinhalt</h3>
                            <div className="space-y-2">
                                <Label htmlFor="aiResponse">KI-Antwort *</Label>
                                <Textarea
                                    id="aiResponse"
                                    value={formData.aiResponse || ''}
                                    onChange={(e) => updateFormData('aiResponse', e.target.value)}
                                    placeholder="Die automatische Antwort, die an den Kunden gesendet wird..."
                                    rows={4}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="notes">Interne Notizen</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes || ''}
                                    onChange={(e) => updateFormData('notes', e.target.value)}
                                    placeholder="Interne Notizen für das Team..."
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Settings */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">Einstellungen</h3>
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <Label>Antwort aktiv</Label>
                                    <p className="text-xs text-muted-foreground">Aktive Antworten werden automatisch verwendet</p>
                                </div>
                                <Switch checked={formData.active} onCheckedChange={(checked: boolean) => updateFormData('active', checked)} />
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <Label>Bearbeitbar für Kunden</Label>
                                    <p className="text-xs text-muted-foreground">Kunden können diese Antwort selbst anpassen</p>
                                </div>
                                <Switch checked={formData.editableByCustomer} onCheckedChange={(checked: boolean) => updateFormData('editableByCustomer', checked)} />
                            </div>
                        </div>
                    </TabsContent>

                    {/* Forwarding Tab */}
                    <TabsContent value="forwarding" className="space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">Weiterleitung konfigurieren</h3>
                            <p className="text-sm text-muted-foreground">
                                Wähle ein Weiterleitungs-Profil aus, um das Gespräch nach dieser Antwort an einen Mitarbeiter weiterzuleiten.
                            </p>

                            {forwardingLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-brand-primary rounded-full" />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Weiterleitungs-Profil</Label>
                                        <Select value={selectedProfileId} onValueChange={handleForwardingChange}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Profil auswählen..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="-1">Keine Weiterleitung</SelectItem>
                                                {profiles.map((profile) => (
                                                    <SelectItem key={profile.id} value={profile.id}>
                                                        {profile.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {selectedProfileId !== '-1' && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 text-green-700">
                                                <Forward className="h-4 w-4" />
                                                <span className="font-medium">Weiterleitung aktiv</span>
                                            </div>
                                            <p className="text-sm text-green-600 mt-1">
                                                Gespräche werden nach dieser Antwort an das ausgewählte Profil weitergeleitet.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* SMS Tab */}
                    <TabsContent value="sms" className="space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">SMS-Konfiguration</h3>
                            <p className="text-sm text-muted-foreground">
                                Konfiguriere eine automatische SMS, die nach dieser Antwort an den Anrufer gesendet wird.
                            </p>

                            {smsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-brand-primary rounded-full" />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-1">
                                            <Label>SMS aktivieren</Label>
                                            <p className="text-xs text-muted-foreground">Automatisch SMS nach dieser Antwort senden</p>
                                        </div>
                                        <Switch
                                            checked={smsConfig.smsActive}
                                            onCheckedChange={(checked) => setSmsConfig(prev => ({ ...prev, smsActive: checked }))}
                                        />
                                    </div>

                                    {smsConfig.smsActive && (
                                        <>
                                            <div className="space-y-2">
                                                <Label>Ansage vor SMS</Label>
                                                <Textarea
                                                    value={smsConfig.utteranceToSay}
                                                    onChange={(e) => setSmsConfig(prev => ({ ...prev, utteranceToSay: e.target.value }))}
                                                    placeholder="z.B. Ich sende Ihnen gleich eine SMS mit allen Details..."
                                                    rows={2}
                                                />
                                                <p className="text-xs text-muted-foreground">Was der Assistent sagt, bevor die SMS gesendet wird</p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>SMS-Text</Label>
                                                <Textarea
                                                    value={smsConfig.messageToSend}
                                                    onChange={(e) => setSmsConfig(prev => ({ ...prev, messageToSend: e.target.value }))}
                                                    placeholder="Der Text der SMS..."
                                                    rows={3}
                                                />
                                            </div>

                                            <Button onClick={handleSmsUpdate} disabled={smsLoading}>
                                                SMS-Konfiguration speichern
                                            </Button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleSave} disabled={isLoading}>
                        {isLoading
                            ? 'Speichere...'
                            : isNewAnswer
                                ? 'Erstellen'
                                : 'Speichern'
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}